import React, { useState, useEffect } from 'react';
import { getCurrencySymbol, formatCurrency, formatCompactCurrency } from '../utils/currency';
import AmountWithNative from '../components/AmountWithNative';
import RecordSettlementModal from '../components/settlements/RecordSettlementModal';
import SettlementPriorHistoryPanel from '../components/settlements/SettlementPriorHistoryPanel';
import { shareInvoicePdf } from '../utils/shareInvoicePdf';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BankTxActions } from '../components/bankfeed/BankTxActions';
import { API, useAuth } from '../App';
import { useNavigationContext } from '../hooks/useNavigationContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import SettlementMethodPicker from '../components/SettlementMethodPicker';
import AppliedCreditDebitNotesPanel from '../components/AppliedCreditDebitNotesPanel';
import SupportingDocsPanel from '../components/documents/SupportingDocsPanel';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  FileText, 
  CheckCircle2, 
  Clock, 
  Download, 
  Mail, 
  Calendar,
  User,
  CreditCard,
  Loader2,
  Copy,
  ExternalLink,
  Link as LinkIcon,
  Send,
  Sparkles,
  Share2,
  MessageCircle,
  Phone,
  Pencil,
  Settings2,
  AlertCircle,
  Building,
  DollarSign,
  Tag
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { CurrencyInput } from '../components/ui/currency-input';
import FXRatePicker from '../components/common/FXRatePicker';
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';

const InvoiceDetailPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { goBack, getReturnLabel, hasReturnUrl } = useNavigationContext();
  const { business } = useAuth();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  // iter 755 - GL Impact (journal entries that touched this invoice)
  const [journalEntries, setJournalEntries] = useState([]);
  const [loadingJournals, setLoadingJournals] = useState(false);
  // iter872 - Derived: the revenue GL account this invoice posts to.
  // Prefer the actual posted revenue line; fall back to invoice.gl_code.
  const revenueGlTile = React.useMemo(() => {
    const revLine = (journalEntries || [])
      .flatMap(j => j.lines || [])
      .find(l => l && parseFloat(l.credit || 0) > 0 && String(l.account_code || '').startsWith('4'));
    return {
      code: revLine?.account_code || invoice?.gl_code || '4000',
      name: revLine?.account_name || 'Sales Revenue',
    };
  }, [journalEntries, invoice?.gl_code]);
  const [markingPaid, setMarkingPaid] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [creatingPaymentLink, setCreatingPaymentLink] = useState(false);
  const [bankAccount, setBankAccount] = useState(null);
  
  // Record Payment Modal State
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showScenarioSettle, setShowScenarioSettle] = useState(false);

  // iter1421 · Auto-open the unified Record Payment modal when the
  // detail page is entered from the Create page with ?openPayment=1
  useEffect(() => {
    if (searchParams.get('openPayment') === '1' && invoice?.id) {
      setShowScenarioSettle(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice?.id]);
  const [partialPaymentAmount, setPartialPaymentAmount] = useState('');
  const [selectedBankId, setSelectedBankId] = useState('');
  // Iter 827 - unified settlement method state (bank OR balance sheet GL)
  const [settlement, setSettlement] = useState({
    settlement_method: 'bank',
    bank_account_id: '',
    settlement_gl_code: null,
  });
  const [bankAccounts, setBankAccounts] = useState([]);
  const [recordingPayment, setRecordingPayment] = useState(false);
  const [payFxRate, setPayFxRate] = useState(null);
  const [showPaymentConfirmDialog, setShowPaymentConfirmDialog] = useState(false);
  
  // Edit GL State
  const [showEditGLDialog, setShowEditGLDialog] = useState(false);
  const [chartOfAccounts, setChartOfAccounts] = useState([]);
  const [editGLLoading, setEditGLLoading] = useState(false);
  const [glCorrection, setGlCorrection] = useState({
    newRevenueAccount: '',
    reason: ''
  });
  
  // iter1114 P3 - removed orphan paymentProviders state (`stripe:false // Coming soon`).
  // Never read anywhere; real Stripe availability comes from invoice.payment_provider
  // and the /stripe-connect/status endpoint used at invoice creation time.

  const formatAmount = (amount, currency) => {
    // iter1091 - no NGN fallback; the currency utility warns in dev when it can't resolve.
    return formatCurrency(amount, currency || business?.currency || '');
  };

  // Check for payment status on page load
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    
    if (paymentStatus === 'success' && sessionId) {
      verifyPayment(sessionId);
    } else if (paymentStatus === 'cancelled') {
      toast.info('Payment was cancelled');
    }
  }, [searchParams]);

  useEffect(() => {
    fetchInvoice();
  }, [id]);

  // Fetch chart of accounts when Edit GL dialog opens
  useEffect(() => {
    if (showEditGLDialog) {
      fetchChartOfAccounts();
    }
  }, [showEditGLDialog]);

  const fetchChartOfAccounts = async () => {
    try {
      const response = await axios.get(`${API}/accounting/chart-of-accounts`);
      setChartOfAccounts(response.data || []);
    } catch (error) {
      console.log('Could not fetch chart of accounts');
    }
  };

  const handleEditGL = async () => {
    if (!glCorrection.reason) {
      toast.error('Please provide a reason for the GL correction');
      return;
    }
    
    const corrections = [];
    
    // Add revenue account correction if changed
    if (glCorrection.newRevenueAccount && glCorrection.newRevenueAccount !== invoice.gl_code) {
      corrections.push({
        old_gl: invoice.gl_code || '4000',
        new_gl: glCorrection.newRevenueAccount,
        type: 'revenue'
      });
    }
    
    // NOTE: Receivable GL changes are intentionally NOT supported here.
    // For invoices the AR control account must remain a single canonical
    // GL (1300) so the AR subledger and GL 1300 always reconcile; allowing
    // per-invoice receivable GL edits would silently break that reconciliation.

    if (corrections.length === 0) {
      toast.error('No GL changes specified');
      return;
    }
    
    setEditGLLoading(true);
    try {
      const response = await axios.post(`${API}/gl-integrity/correct-gl`, {
        document_type: 'invoice',
        document_id: id,
        corrections: corrections,
        reason: glCorrection.reason
      });
      
      if (response.data.success) {
        toast.success('GL accounts corrected successfully');
        setShowEditGLDialog(false);
        setGlCorrection({ newRevenueAccount: '', reason: '' });
        fetchInvoice(); // Refresh to show updated data
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to correct GL accounts');
    } finally {
      setEditGLLoading(false);
    }
  };

  const verifyPayment = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/stripe-connect/payment-status/${sessionId}`);
      if (response.data.payment_status === 'paid') {
        toast.success('Payment received successfully!');
        fetchInvoice(); // Refresh invoice to show paid status
      }
    } catch (error) {
      console.log('Payment verification:', error);
    }
  };

  const createPaymentLink = async (provider = 'stripe') => {
    setCreatingPaymentLink(true);
    try {
      let response;
      let paymentUrl;

      // iter 933 - Backend route is /invoices/{id}/payment-link (under payments router,
      // mounted at /api with no extra prefix), NOT /payments/invoices/{id}/payment-link.
      response = await axios.post(`${API}/invoices/${id}/payment-link`);
      paymentUrl = response.data.payment_url;
      setInvoice(prev => ({ ...prev, payment_link: paymentUrl }));

      toast.success('Stripe payment link created!');
      
      // Copy to clipboard
      navigator.clipboard.writeText(paymentUrl);
      toast.info('Payment link copied to clipboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to create ${provider} payment link`);
    } finally {
      setCreatingPaymentLink(false);
    }
  };

  const fetchInvoice = async () => {
    try {
      const response = await axios.get(`${API}/invoices/${id}`);
      setInvoice(response.data);

      // iter 755 - Fetch the actual GL impact (journal lines) so we can
      // surface Revenue GL, AR, Bank, VAT etc. in a dedicated card.
      setLoadingJournals(true);
      axios.get(`${API}/invoices/${id}/journal-entries`)
        .then((r) => setJournalEntries(r.data?.entries || []))
        .catch(() => setJournalEntries([]))
        .finally(() => setLoadingJournals(false));
      
      // Fetch bank account details if specified
      if (response.data.bank_account_id) {
        try {
          const bankRes = await axios.get(`${API}/bank-accounts`);
          const allBanks = bankRes.data || [];
          setBankAccounts(allBanks);
          const matchedBank = allBanks.find(b => b.id === response.data.bank_account_id);
          if (matchedBank) {
            setBankAccount(matchedBank);
            setSelectedBankId(matchedBank.id);
          }
        } catch (err) {
          console.log('Could not fetch bank account');
        }
      } else {
        // Still fetch bank accounts for payment recording
        try {
          const bankRes = await axios.get(`${API}/bank-accounts`);
          setBankAccounts(bankRes.data || []);
        } catch (err) {
          console.log('Could not fetch bank accounts');
        }
      }
    } catch (error) {
      toast.error('Invoice not found');
      navigate('/invoices');
    } finally {
      setLoading(false);
    }
  };

  const markAsPaid = async () => {
    setMarkingPaid(true);
    try {
      await axios.patch(`${API}/invoices/${id}/mark-paid`);
      toast.success('Invoice marked as paid!');
      fetchInvoice();
    } catch (error) {
      toast.error('Failed to update invoice');
    } finally {
      setMarkingPaid(false);
    }
  };

  const downloadPDF = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API}/invoices/${id}/pdf`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${invoice.invoice_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Invoice PDF downloaded!');
    } catch (error) {
      toast.error('Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };

  const copyPaymentLink = () => {
    if (invoice.payment_link) {
      navigator.clipboard.writeText(invoice.payment_link);
      toast.success('Payment link copied!');
    }
  };

  // Share functionality
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  
  // Get bank account for payment reference
  const getPaymentReference = () => `REF-${invoice.invoice_number}`;
  
  const getShareMessage = () => {
    const currencySymbol = getCurrencySymbol(invoice.currency);
    const amount = `${currencySymbol}${(invoice.amount || 0).toLocaleString()}`;
    const paymentRef = getPaymentReference();
    
    const bizName = business?.business_name || 'Team';
    const dueDate = new Date(invoice.due_date).toLocaleDateString();
    const divider = '--------------------------------';

    let message = `Dear ${invoice.customer_name},\n\n`;
    message += `Please find invoice *${invoice.invoice_number}* from ${bizName}.\n\n`;
    message += `Amount due: *${amount}*\n`;
    message += `Due date: ${dueDate}\n\n`;

    // Line items summary
    if (invoice.line_items?.length > 0) {
      message += `${divider}\nITEMS\n${divider}\n`;
      invoice.line_items.forEach(item => {
        const itemTotal = item.amount * (item.quantity || 1);
        message += `• ${item.description} (x${item.quantity || 1}): ${currencySymbol}${itemTotal.toLocaleString()}\n`;
      });
      message += '\n';
    }

    if (invoice.payment_link) {
      message += `Pay online:\n${invoice.payment_link}\n\n`;
    }

    // Bank transfer details
    if (bankAccount) {
      message += `${divider}\nBANK TRANSFER DETAILS\n${divider}\n`;
      message += `Bank: ${bankAccount.bank_name}\n`;
      message += `Account name: ${bankAccount.account_name}\n`;
      message += `Account number: ${bankAccount.account_number}\n`;
      if (bankAccount.sort_code) message += `Sort code: ${bankAccount.sort_code}\n`;
      if (bankAccount.iban) message += `IBAN: ${bankAccount.iban}\n`;
      if (bankAccount.bic_swift) message += `BIC/SWIFT: ${bankAccount.bic_swift}\n`;
      if (bankAccount.routing_number) message += `Routing number: ${bankAccount.routing_number}\n`;
      message += `Payment reference: ${paymentRef}\n\n`;
      message += `Please include the reference "${paymentRef}" with your payment so we can match it to this invoice.\n\n`;
    }

    message += `Thank you for your business!\n${bizName}`;

    return message;
  };

  const getShortShareMessage = () => {
    const currencySymbol = getCurrencySymbol(invoice.currency);
    const amount = `${currencySymbol}${(invoice.amount || 0).toLocaleString()}`;
    const paymentRef = getPaymentReference();
    return `Invoice ${invoice.invoice_number} - ${amount}\nDue: ${new Date(invoice.due_date).toLocaleDateString()}\nRef: ${paymentRef}${invoice.payment_link ? `\nPay: ${invoice.payment_link}` : ''}`;
  };

  // Native share via Web Share API - util handles iOS gesture-expiry (the
  // cached PDF is shared instantly on the follow-up tap) and download fallbacks.
  const shareInvoice = () => shareInvoicePdf({
    cacheKey: invoice.id,
    fileName: `Invoice_${invoice.invoice_number}.pdf`,
    title: `Invoice ${invoice.invoice_number}`,
    text: getShareMessage(),
    url: invoice.payment_link || window.location.href,
    getBlob: async () => (await axios.get(`${API}/invoices/${invoice.id}/pdf`, { responseType: 'blob' })).data,
    downloadFallback: () => downloadPDF(),
  });

  const shareViaEmail = () => {
    const subject = encodeURIComponent(`Invoice ${invoice.invoice_number} from ${business?.business_name || 'Your Business'}`);
    const body = encodeURIComponent(getShareMessage());
    window.open(`mailto:${invoice.customer_email || ''}?subject=${subject}&body=${body}`, '_blank');
    toast.success('Opening email client...');
  };

  const shareViaSMS = () => {
    const message = encodeURIComponent(getShareMessage());
    window.open(`sms:?body=${message}`, '_blank');
    toast.success('Opening SMS...');
  };

  const copyAllDetails = () => {
    navigator.clipboard.writeText(getShareMessage());
    toast.success('Invoice details copied to clipboard!');
  };

  // Email Preview Component
  const EmailPreviewContent = () => (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden max-w-lg mx-auto">
      {/* Email Header */}
      <div className="bg-gradient-to-r from-[#283F8B] to-[#108981] p-6 text-white text-center">
        <h2 className="text-2xl font-bold">{business?.business_name || 'Your Business'}</h2>
        <p className="text-white/80 text-sm mt-1">Invoice #{invoice.invoice_number}</p>
      </div>
      
      {/* Email Body */}
      <div className="p-6">
        <p className="text-gray-700 mb-4">Dear {invoice.customer_name},</p>
        <p className="text-gray-600 mb-6">Please find below your invoice details:</p>
        
        {/* Amount Card */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6 text-center">
          <p className="text-sm text-gray-500 mb-1">Amount Due</p>
          <p className="text-4xl font-bold text-[#108981]">
            {getCurrencySymbol(invoice.currency)}{(invoice.amount || 0).toLocaleString()}
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Due by {new Date(invoice.due_date).toLocaleDateString()}
          </p>
        </div>
        
        {/* Line Items */}
        {invoice.line_items?.length > 0 && (
          <div className="mb-6">
            <p className="text-sm font-medium text-gray-700 mb-2">Items:</p>
            <div className="border rounded-lg overflow-hidden">
              {invoice.line_items.map((item, idx) => (
                <div key={idx} className={`p-3 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-3">
                    {item.image_url && (
                      <img 
                        src={item.image_url} 
                        alt={item.description} 
                        className="w-12 h-12 object-cover rounded-lg border border-gray-200"
                      />
                    )}
                    <div className="flex-1 flex justify-between items-center">
                      <div className="min-w-0">
                        <span className="text-gray-700">{item.description}</span>
                        {item.item_description && (
                          <p className="text-xs text-gray-500 mt-0.5 whitespace-pre-line" data-testid="invoice-line-item-description">
                            {item.item_description}
                          </p>
                        )}
                      </div>
                      <span className="font-medium">{getCurrencySymbol(invoice.currency)}{(item.amount * (item.quantity || 1)).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Payment Button */}
        {invoice.payment_link && (
          <div className="text-center mb-6">
            <a 
              href={invoice.payment_link}
              className="inline-block bg-[#108981] text-white font-semibold py-3 px-8 rounded-lg hover:bg-[#108981]/90 transition-colors"
            >
              Pay Now
            </a>
            <p className="text-xs text-gray-400 mt-2" data-testid="invoice-payment-provider-tag">
              Secure payment via Stripe
            </p>
          </div>
        )}
        
        {/* Bank Transfer Details */}
        {bankAccount && (
          <div className="bg-blue-50 rounded-lg p-4 mb-6">
            <p className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              🏦 Bank Transfer Details
            </p>
            <div className="space-y-1 text-sm text-gray-600">
              <p><span className="font-medium">Bank:</span> {bankAccount.bank_name}</p>
              <p><span className="font-medium">Account Name:</span> {bankAccount.account_name}</p>
              <p><span className="font-medium">Account Number:</span> {bankAccount.account_number}</p>
              {bankAccount.sort_code && <p><span className="font-medium">Sort Code:</span> {bankAccount.sort_code}</p>}
              {bankAccount.iban && <p><span className="font-medium">IBAN:</span> {bankAccount.iban}</p>}
              {bankAccount.bic_swift && <p><span className="font-medium">BIC/SWIFT:</span> {bankAccount.bic_swift}</p>}
              {bankAccount.routing_number && <p><span className="font-medium">Routing Number:</span> {bankAccount.routing_number}</p>}
            </div>
            <div className="mt-3 p-2 bg-amber-100 rounded text-xs text-amber-800">
              <strong>Important:</strong> Please use reference <strong>{getPaymentReference()}</strong> when making your transfer
            </div>
          </div>
        )}
        
        {/* Download PDF Button */}
        <div className="text-center">
          <button 
            onClick={downloadPDF}
            className="text-[#283F8B] text-sm font-medium hover:underline inline-flex items-center gap-1"
          >
            <Download className="w-4 h-4" /> Download PDF Invoice
          </button>
        </div>
      </div>
      
      {/* Email Footer */}
      <div className="bg-gray-50 p-4 text-center border-t">
        <p className="text-xs text-gray-500">
          This invoice was sent from {business?.business_name || 'AkountFly'}
        </p>
        <p className="text-xs text-gray-400 mt-1">
          Powered by AkountFly - Virtual Accountant
        </p>
      </div>
    </div>
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-4 sm:p-6 lg:p-8 flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#108981]" />
        </div>
      </DashboardLayout>
    );
  }

  if (!invoice) return null;

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto" data-testid="invoice-detail-container">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => goBack('/invoices')}
            className="self-start"
            data-testid="back-btn"
            title={hasReturnUrl() ? `Back to ${getReturnLabel()}` : 'Back to Invoices'}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <h1 className="text-xl sm:text-2xl font-bold text-foreground cursor-help">{invoice.invoice_number}</h1>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs">
                    <p className="text-xs">Ask your customer to include <span className="font-mono font-bold">{invoice.invoice_number}</span> in their payment reference for automatic matching</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <Badge variant={invoice.status === 'paid' ? 'default' : 'secondary'} className={
                invoice.status === 'paid' ? 'bg-[#108981]' : 
                invoice.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                'bg-amber-100 text-amber-700'
              }>
                {invoice.status}
              </Badge>
              {/* Opening Balance Badge */}
              {(invoice.is_opening_balance || invoice.source === 'migration') && (
                <Badge 
                  variant="outline" 
                  className="bg-purple-50 text-purple-700 border-purple-200"
                  data-testid="opening-balance-badge"
                >
                  Opening Balance - Read Only
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground">For {invoice.customer_name}</p>
            {/* Bank lines that created OR settled this invoice (renders nothing when none) */}
            <BankTxActions documentId={invoice.id} allowUndo onUndone={() => window.location.reload()} className="mt-1" />
          </div>
          <div className="flex flex-wrap gap-2 w-full sm:w-auto">
            {/* Bank Feed Source Banner */}
            {invoice.source === 'bank_feed' && (
              <div className="w-full mb-2 p-2 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2 text-sm text-blue-700">
                <AlertCircle className="w-4 h-4" />
                <span>This invoice was created from a bank feed. Edit via <a href="/bank-feeds" className="underline font-medium">Bank Transactions</a>.</span>
              </div>
            )}
            
            {/* Edit Invoice Button - Disabled for opening balance AND bank feed documents */}
            <Button 
              variant="outline" 
              className="gap-2 flex-1 sm:flex-none"
              onClick={() => navigate(`/invoices/${id}/edit`)}
              disabled={invoice.is_opening_balance || invoice.source === 'migration' || invoice.source === 'bank_feed'}
              data-testid="edit-invoice-btn"
              title={
                invoice.source === 'bank_feed' 
                  ? 'Edit this document via Bank Transactions' 
                  : invoice.is_opening_balance || invoice.source === 'migration' 
                    ? 'Opening balance invoices cannot be edited' 
                    : ''
              }
            >
              <Pencil className="w-4 h-4" />
              <span className="hidden sm:inline">Edit Invoice</span>
            </Button>
            
            {/* Edit GL Button - Disabled for opening balance AND bank feed documents */}
            <Button 
              variant="outline" 
              className="gap-2 flex-1 sm:flex-none"
              onClick={() => setShowEditGLDialog(true)}
              disabled={invoice.is_opening_balance || invoice.source === 'migration' || invoice.source === 'bank_feed'}
              data-testid="edit-gl-btn"
              title={
                invoice.source === 'bank_feed' 
                  ? 'Edit this document via Bank Transactions' 
                  : invoice.is_opening_balance || invoice.source === 'migration' 
                    ? 'Opening balance invoices cannot be edited' 
                    : ''
              }
            >
              <Settings2 className="w-4 h-4" />
              <span className="hidden sm:inline">Edit GL</span>
            </Button>
            
            {/* Share Button - Uses Native Device Sharing */}
            <Button 
              variant="outline" 
              className="gap-2 flex-1 sm:flex-none"
              data-testid="share-invoice-btn"
              onClick={shareInvoice}
            >
              <Share2 className="w-4 h-4" />
              <span className="hidden sm:inline">Share</span>
            </Button>
            
            {/* Email Preview Dialog */}
            <Dialog open={showEmailPreview} onOpenChange={setShowEmailPreview}>
              <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Mail className="w-5 h-5 text-[#283F8B]" />
                    Customer Invoice View
                  </DialogTitle>
                </DialogHeader>
                <div className="py-4">
                  <p className="text-sm text-muted-foreground mb-4 text-center">
                    This is what your customer will see when they receive the invoice:
                  </p>
                  <EmailPreviewContent />
                </div>
              </DialogContent>
            </Dialog>
            
            <Button 
              variant="outline" 
              className="gap-2 flex-1 sm:flex-none"
              onClick={downloadPDF}
              disabled={downloading}
            >
              {downloading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              <span className="hidden sm:inline">Download</span> PDF
            </Button>
            {invoice.status !== 'paid' && (
              <>
                {/* iter1421 · Unified Record Payment — opens the same
                    RecordSettlementModal used by bank feeds and the
                    bill/invoice list views. Replaces the legacy inline
                    Record Payment dialog, quick "Mark Paid" shortcut,
                    and "Settle by Scenario" buttons. */}
                <Button
                  className="bg-[#108981] hover:bg-[#108981]/90 gap-2 flex-1 sm:flex-none"
                  onClick={() => setShowScenarioSettle(true)}
                  disabled={invoice.source === 'bank_feed'}
                  data-testid="mark-paid-btn"
                  title={invoice.source === 'bank_feed'
                    ? 'Settlements on bank-feed docs must be posted via the Bank Feed Centre'
                    : 'Record a payment against this invoice — WHT, partial, full or FX-aware.'}
                >
                  <CheckCircle2 className="w-4 h-4" />
                  Record Payment
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Amount Card */}
          <Card className="lg:col-span-2">
            <CardContent className="p-6">
              <div className="flex items-center gap-4 mb-6">
                <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center ${
                  invoice.status === 'paid' ? 'bg-[#108981]/10' : 'bg-amber-50'
                }`}>
                  {invoice.status === 'paid' ? (
                    <CheckCircle2 className="w-7 h-7 sm:w-8 sm:h-8 text-[#108981]" />
                  ) : (
                    <Clock className="w-7 h-7 sm:w-8 sm:h-8 text-amber-500" />
                  )}
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Amount</p>
                  <AmountWithNative
                    native={invoice.total_amount || invoice.amount}
                    nativeCurrency={invoice.currency}
                    functional={invoice.is_foreign_currency ? invoice.base_currency_amount : null}
                    functionalCurrency={invoice.base_currency || business?.currency}
                    exchangeRate={invoice.fx_rate}
                    exchangeRateDate={invoice.exchange_rate_date || invoice.invoice_date}
                    primarySize="text-3xl sm:text-4xl font-bold text-foreground"
                    secondarySize="text-xs text-blue-600 font-medium mt-1"
                    align="left"
                    testId="invoice-detail-amount"
                  />
                </div>
              </div>

              {/* Line Items */}
              {invoice.line_items?.length > 0 && (
                <div className="mb-6">
                  <p className="text-sm font-medium text-muted-foreground mb-3">Line Items</p>
                  <div className="space-y-2">
                    {invoice.line_items.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                        {item.image_url && (
                          <img 
                            src={item.image_url} 
                            alt={item.description} 
                            className="w-14 h-14 object-cover rounded-lg border border-border flex-shrink-0"
                          />
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="font-medium truncate">{item.description}</p>
                          {item.item_description && (
                            <p className="text-xs text-muted-foreground mt-0.5 whitespace-pre-line line-clamp-3" data-testid="invoice-line-item-description">
                              {item.item_description}
                            </p>
                          )}
                          <p className="text-sm text-muted-foreground">Qty: {item.quantity || 1}</p>
                        </div>
                        <p className="font-semibold ml-4 flex-shrink-0">
                          {formatAmount(item.amount * (item.quantity || 1), invoice.currency)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                  <Calendar className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">Due Date</p>
                    <p className="font-medium truncate">{new Date(invoice.due_date).toLocaleDateString()}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                  <Clock className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">Created</p>
                    <p className="font-medium truncate">{new Date(invoice.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
                {invoice.paid_at && (
                  <div className="flex items-center gap-3 p-3 bg-[#108981]/5 rounded-lg sm:col-span-2">
                    <CheckCircle2 className="w-5 h-5 text-[#108981] flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground">Paid On</p>
                      <p className="font-medium text-[#108981] truncate">{new Date(invoice.paid_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* iter872 - GL Category (compact, matches Bill Detail page style).
              Replaces the prior multi-row Unified Journal table: full
              journal details remain available via the "View Journal" link
              on the Reports → Aged Receivables page. */}
          <Card data-testid="invoice-gl-impact-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Tag className="w-4 h-4" />
                GL Category
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg" data-testid="invoice-gl-category-tile">
                <Tag className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">Posts to revenue account</p>
                  <p className="font-medium truncate">
                    <span className="font-mono text-muted-foreground mr-2">{revenueGlTile.code}</span>
                    {revenueGlTile.name}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* iter872 - Applied Credit Notes (inline, with Unapply action) */}
          <AppliedCreditDebitNotesPanel
            document={invoice}
            kind="credit"
            onChange={fetchInvoice}
          />

          {/* iter1086h - Supporting docs (unified backbone). Uploads here
              are auto-synced to any bank-feed transaction matched to
              this invoice via cross-channel sync (see routers/docs_sync). */}
          {invoice?.id && (
            <SupportingDocsPanel
              entityType="invoice"
              entityId={invoice.id}
              uploadedVia="invoice"
              className="mt-4"
            />
          )}

          {/* Customer Info */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <User className="w-4 h-4" />
                Customer
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="font-semibold text-lg break-words">{invoice.customer_name}</p>
                {invoice.customer_email && (
                  <p className="text-sm text-muted-foreground break-all">{invoice.customer_email}</p>
                )}
              </div>
              
              {invoice.payment_link && (
                <div className="pt-3 border-t">
                  <p className="text-xs text-muted-foreground mb-2">Payment Link</p>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1 gap-1"
                      onClick={copyPaymentLink}
                      data-testid="invoice-copy-payment-link-btn"
                    >
                      <Copy className="w-3 h-3" />
                      <span className="hidden sm:inline">Copy</span>
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1 gap-1"
                      onClick={() => window.open(invoice.payment_link, '_blank')}
                      data-testid="invoice-open-payment-link-btn"
                    >
                      <ExternalLink className="w-3 h-3" />
                      <span className="hidden sm:inline">Open</span>
                    </Button>
                    {/* iter1114 P0.3 - one-tap WhatsApp share with link */}
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 gap-1"
                      onClick={() => {
                        const msg = `Hi ${invoice.customer_name || 'there'}, please pay Invoice ${invoice.invoice_number || ''}: ${invoice.payment_link}`;
                        window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
                      }}
                      data-testid="invoice-whatsapp-payment-link-btn"
                    >
                      <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M20.52 3.48A11.9 11.9 0 0 0 12.06 0C5.55 0 .28 5.27.28 11.78c0 2.07.54 4.09 1.57 5.88L0 24l6.5-1.7a11.75 11.75 0 0 0 5.56 1.41h.01c6.51 0 11.78-5.27 11.78-11.78 0-3.14-1.22-6.1-3.33-8.45zm-8.46 18.13h-.01a9.83 9.83 0 0 1-5.01-1.37l-.36-.21-3.86 1.01 1.03-3.76-.23-.38a9.79 9.79 0 0 1-1.5-5.22c0-5.42 4.42-9.83 9.85-9.83 2.63 0 5.1 1.02 6.97 2.88a9.77 9.77 0 0 1 2.88 6.96c0 5.42-4.42 9.83-9.83 9.83z"/></svg>
                      <span className="hidden sm:inline">WhatsApp</span>
                    </Button>
                  </div>
                </div>
              )}

              {/* iter1114 P0.3 - Generate & Copy button when no link exists yet
                  and Stripe is fully connected. Uses the on-demand backend
                  endpoint which correctly reads db.stripe_connect. */}
              {!invoice.payment_link && invoice.payment_provider === 'stripe' && invoice.status !== 'paid' && (
                <div className="pt-3 border-t">
                  <p className="text-xs text-muted-foreground mb-2">Payment Link</p>
                  {/* iter1221 (Feb 2026) - Show why the auto-generation failed, if it did.
                      iter1222 - added edit-invalidated variant. */}
                  {invoice.payment_link_error && (
                    <div className="mb-2 p-2 rounded border border-red-200 bg-red-50 text-xs text-red-700" data-testid="invoice-payment-link-error-banner">
                      {invoice.payment_link_error === 'invoice_edited_regenerate_required' ? (
                        <>
                          <span className="font-medium">This invoice was edited after the payment link was created.</span>
                          <span className="block mt-0.5 text-red-600/80">
                            The old checkout was expired so customers can&apos;t pay a stale total. Click Retry to mint a fresh link with the updated amount.
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="font-medium">Stripe could not generate the link on save.</span>
                          {invoice.payment_link_error !== 'stripe_not_fully_connected' && (
                            <span className="block mt-0.5 text-red-600/80 break-all">{invoice.payment_link_error}</span>
                          )}
                          {invoice.payment_link_error === 'stripe_not_fully_connected' && (
                            <span className="block mt-0.5 text-red-600/80">
                              Complete your Stripe Connect onboarding, then click Retry below.
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  )}
                  <Button
                    variant="default"
                    size="sm"
                    className="w-full gap-1 bg-[#635BFF] hover:bg-[#4f46e5]"
                    onClick={() => createPaymentLink('stripe')}
                    disabled={creatingPaymentLink}
                    data-testid="invoice-generate-payment-link-btn"
                  >
                    {creatingPaymentLink ? <Loader2 className="w-3 h-3 animate-spin" /> : <CreditCard className="w-3 h-3" />}
                    {creatingPaymentLink
                      ? 'Generating...'
                      : (invoice.payment_link_error ? 'Retry Stripe Payment Link' : 'Generate & Copy Stripe Payment Link')}
                  </Button>
                  <p className="text-xs text-muted-foreground mt-2">
                    Creates a secure Stripe checkout URL for this invoice and copies it to your clipboard.
                  </p>
                </div>
              )}

              {invoice.payment_reference && (
                <div className="pt-3 border-t">
                  <p className="text-xs text-muted-foreground">Payment Reference</p>
                  <p className="font-mono text-sm break-all">{invoice.payment_reference}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          {invoice.description && (
            <Card className="lg:col-span-3">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Notes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">{invoice.description}</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Edit GL Dialog */}
      <Dialog open={showEditGLDialog} onOpenChange={setShowEditGLDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings2 className="w-5 h-5 text-[#108981]" />
              Edit GL Accounts
            </DialogTitle>
            <DialogDescription>
              Correct the General Ledger accounts for this invoice. This will create a reversal journal entry and a new corrected entry.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Current GL Info */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-amber-800">Current GL Assignment</p>
                  <p className="text-amber-700">Revenue: {invoice?.gl_code || '4000'} (Sales Revenue)</p>
                  <p className="text-amber-700">
                    Receivable: 1300 (Accounts Receivable)
                    <span className="text-xs text-amber-600/80 ml-1">(locked, cannot be changed)</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Revenue Account Selection */}
            <div className="space-y-2">
              <Label>New Revenue Account</Label>
              <Select 
                value={glCorrection.newRevenueAccount} 
                onValueChange={(value) => setGlCorrection(prev => ({ ...prev, newRevenueAccount: value }))}
              >
                <SelectTrigger data-testid="invoice-edit-gl-revenue-select">
                  <SelectValue placeholder="Select revenue account" />
                </SelectTrigger>
                <SelectContent>
                  {chartOfAccounts
                    .filter(acc => acc.type === 'revenue' || acc.code?.startsWith('4'))
                    .map(acc => (
                      <SelectItem key={acc.code} value={acc.code}>
                        {acc.code} - {acc.name}
                      </SelectItem>
                    ))
                  }
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Only the revenue GL can be edited here. The Accounts Receivable control account
                is locked so the AR subledger always reconciles with GL 1300.
              </p>
            </div>

            {/* Reason */}
            <div className="space-y-2">
              <Label>Reason for Correction *</Label>
              <Textarea
                value={glCorrection.reason}
                onChange={(e) => setGlCorrection(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="e.g., Incorrect revenue classification - should be consulting income"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowEditGLDialog(false)}
              disabled={editGLLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleEditGL}
              disabled={!glCorrection.reason || editGLLoading}
              className="bg-[#108981] hover:bg-[#0a6b65]"
            >
              {editGLLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Apply GL Correction
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter1421 · Unified Record Payment modal — replaces the legacy
          Payment Dialog + Mark as Paid one-click + Settle by Scenario.
          Same modal is used by list/detail/edit pages. */}
      {invoice && (
        <RecordSettlementModal
          open={showScenarioSettle}
          onOpenChange={setShowScenarioSettle}
          doc={{
            id: invoice.id,
            doc_type: 'invoice',
            currency: invoice.currency,
            outstanding: invoice.balance_due ?? Math.max(0, (invoice.total_amount || invoice.amount || 0) - (invoice.amount_paid || 0)),
            balance_due: invoice.balance_due,
            total_amount: invoice.total_amount || invoice.amount,
            amount_paid: invoice.amount_paid,
          }}
          direction="inflow"
          functionalCurrency={business?.currency}
          suggestedBankAccountId={invoice?.bank_account_id || invoice?.deposit_bank_account_id || null}
          title="Record Payment"
          subtitle={invoice?.invoice_number ? `Invoice ${invoice.invoice_number}` : undefined}
          onSettled={() => { fetchInvoice(); }}
        />
      )}
    </DashboardLayout>
  );
};

export default InvoiceDetailPage;
